import React, { useState } from "react";
import Table from "./Table";
import { agentApi } from "../../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../../lib/alerts";
import { downloadBlob } from "../../lib/catalog";
import { getErrorMessage } from "../../lib/apiMessages";

function formatImportError(error) {
  if (!error) return "-";
  const key = String(error);
  if (/^[A-Z][A-Z0-9_]+$/.test(key)) {
    return getErrorMessage(key);
  }
  return key;
}

export default function BulkPassengerUpload({ packageId, bookingId, remainingSlots = 0, onImported }) {
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const invalidRows = rows.filter((row) => row.error);
  const validRows = rows.filter((row) => !row.error);
  const canImport = Boolean(file) && !saving && packageId && bookingId;

  async function downloadTemplate() {
    if (!packageId || !bookingId) {
      showErrorAction("PASSENGER_BOOKING_REQUIRED");
      return;
    }
    try {
      setDownloading(true);
      const blob = await agentApi.get(
        `/umrah-packages/${packageId}/bookings/${bookingId}/passengers/template`,
        { responseType: "blob" }
      );
      downloadBlob(blob, "passenger-template.xlsx");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_PASSENGER_TEMPLATE");
    } finally {
      setDownloading(false);
    }
  }

  function onFileChange(event) {
    const nextFile = event.target.files?.[0];
    event.target.value = "";
    setRows([]);
    setParseError("");
    setFileName("");
    setFile(null);
    if (!nextFile) return;

    const name = nextFile.name || "passengers.xlsx";
    if (!/\.(csv|xlsx)$/i.test(name)) {
      showErrorAction("PASSENGER_CSV_EMPTY");
      return;
    }
    if (nextFile.size > 2 * 1024 * 1024) {
      showErrorAction("DOCUMENT_TOO_LARGE");
      return;
    }
    if (remainingSlots > 0) {
      setParseError("");
    }
    setFileName(name);
    setFile(nextFile);
  }

  async function importRows() {
    if (!canImport) {
      showErrorAction("PASSENGER_CSV_EMPTY");
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("file", file);
      const payload = await agentApi.post(
        `/umrah-packages/${packageId}/bookings/${bookingId}/passengers/import`,
        { body: formData }
      );
      const result = payload?.data || payload || {};
      const importedRows = Array.isArray(result.rows) ? result.rows : [];
      setRows(importedRows);
      const invalid = importedRows.filter((row) => row.error);
      if (invalid.length > 0) {
        setParseError(getErrorMessage("PASSENGER_CSV_INVALID"));
      } else {
        setParseError("");
      }
      if (Number(result.imported || 0) > 0) {
        setFile(null);
        setFileName("");
        showSuccessAction("IMPORT_PASSENGERS");
        if (onImported) await onImported();
      } else if (invalid.length > 0) {
        showErrorAction("PASSENGER_CSV_INVALID");
      } else {
        showErrorAction("PASSENGER_CSV_EMPTY");
      }
    } catch (saveError) {
      showApiError(saveError, "IMPORT_PASSENGERS");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border rounded-3 p-3 mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div>
          <h6 className="mb-1">Bulk upload (Excel or CSV)</h6>
          <p className="small text-secondary mb-0">
            Download the template, then upload an XLSX or CSV file. Valid rows are imported and invalid rows are listed below.
          </p>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" disabled={downloading || !packageId || !bookingId} onClick={downloadTemplate}>
          {downloading ? "Downloading..." : "Download template"}
        </button>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <input
          type="file"
          className="form-control"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onFileChange}
          aria-label="Passenger spreadsheet"
        />
        <button type="button" className="btn btn-primary" disabled={!canImport} onClick={importRows}>
          {saving ? "Importing..." : "Import passengers"}
        </button>
      </div>
      {fileName ? <div className="small text-secondary mt-2">{fileName}</div> : null}
      {parseError ? <div className="text-danger small mt-2" role="alert">{parseError}</div> : null}
      {invalidRows.length > 0 ? (
        <div className="mt-3 table-responsive">
          <Table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Name</th>
                <th>Passport</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              {invalidRows.map((row) => (
                <tr key={row.line}>
                  <td>{row.line}</td>
                  <td>{[row.passenger?.uppd_fname, row.passenger?.uppd_lname].filter(Boolean).join(" ") || "-"}</td>
                  <td>{row.passenger?.uppd_passport_no || "-"}</td>
                  <td>{formatImportError(row.error)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}
      {validRows.length > 0 && invalidRows.length > 0 ? (
        <div className="small text-success mt-2">{validRows.length} valid row{validRows.length === 1 ? "" : "s"} imported.</div>
      ) : null}
    </div>
  );
}
